import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Record, RecordCategory, RecordStatus, PriceType, SourceType, AffiliateLink } from '../../types/records';
import { getRecordById, addRecord, updateRecord } from '../../services/recordService';
import { uploadImage, uploadMultipleImages } from '../../services/imageUploadService';
import { format, isValid } from 'date-fns';

const adminPath = process.env.REACT_APP_ADMIN_PATH || '/sh-yonetim-kapisi-x7';

const provinces = ['Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Amasya', 'Ankara', 'Antalya', 'Artvin', 'Aydın', 'Balıkesir', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa', 'Çanakkale', 'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Edirne', 'Elazığ', 'Erzincan', 'Erzurum', 'Eskişehir', 'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkâri', 'Hatay', 'Isparta', 'Mersin', 'İstanbul', 'İzmir', 'Kars', 'Kastamonu', 'Kayseri', 'Kırklareli', 'Kırşehir', 'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa', 'Kahramanmaraş', 'Mardin', 'Muğla', 'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Rize', 'Sakarya', 'Samsun', 'Siirt', 'Sinop', 'Sivas', 'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Şanlıurfa', 'Uşak', 'Van', 'Yozgat', 'Zonguldak', 'Aksaray', 'Bayburt', 'Karaman', 'Kırıkkale', 'Batman', 'Şırnak', 'Bartın', 'Ardahan', 'Iğdır', 'Yalova', 'Karabük', 'Kilis', 'Osmaniye', 'Düzce'];

const categories: RecordCategory[] = ['Etkinlikler', 'Doğa Güzellikleri', 'Konserler', 'Beklenmedik Olaylar', 'Devletçe Kapalı', 'Popüler Olanlar', 'Rehber'];
const statuses: RecordStatus[] = ['AÇIK', 'KAPALI', 'DİKKAT', 'İPTAL'];
const priceTypes: PriceType[] = ['Ücretsiz', 'Ücretli', 'Karışık'];
const sourceTypes: SourceType[] = ['Resmi', 'Topluluk', 'Sosyal Medya'];

const AddEditContentPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<Partial<Record>>({
    title: '',
    summary: '',
    content: '',
    coverImage: '',
    galleryImages: [],
    cityName: '',
    districtName: '',
    category: 'Etkinlikler',
    status: 'AÇIK',
    priceType: 'Ücretsiz',
    sourceType: 'Resmi',
    isFeatured: false,
    affiliateLinks: [],
  });

  const [isTimeless, setIsTimeless] = useState<boolean>(false);
  const [slug, setSlug] = useState<string>('');
  const [summaryCharCount, setSummaryCharCount] = useState(0);
  const [galleryUploading, setGalleryUploading] = useState(false);

  // UI için preview state'i, ama gerçek veri formData.coverImage
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      const existingRecord = getRecordById(id);
      if (existingRecord) {
        const data = {
          ...existingRecord,
          content: existingRecord.content || '',
          coverImage: existingRecord.coverImage || '',
          galleryImages: existingRecord.galleryImages || [],
          startDate: existingRecord.startDate,
          endDate: existingRecord.endDate,
          affiliateLinks: existingRecord.affiliateLinks || [],
        };
        setFormData(data);
        setSlug(id);
        setIsTimeless(!existingRecord.startDate && !existingRecord.endDate);
        setSummaryCharCount(existingRecord.summary?.length || 0);

        // Preview'u mevcut kayıtla başlat
        setCoverImagePreview(existingRecord.coverImage || null);
      } else {
        alert('Kayıt bulunamadı!');
        navigate(`${adminPath}/all-content`);
      }
    }
  }, [id, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'title') {
      setSlug(value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-*|-*$/g, ''));
    }
    if (name === 'summary') {
      setSummaryCharCount(value.length);
    }
  };

  const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB

  const handleCoverImageFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      alert('Yükleme hatası: Dosya boyutu 15MB\'den küçük olmalı');
      return;
    }

    // UI için hemen önizleme göster (kullanıcı beklerken)
    const reader = new FileReader();
    reader.onloadend = () => {
      setCoverImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Arka planda Firebase'e yükle
    console.log("Firebase yüklemesi başlatılıyor...");

    try {
      const url = await uploadImage(file, 'blog-covers');
      console.log("Firebase yüklemesi BAŞARILI. URL:", url);
      setFormData(prev => ({
        ...prev,
        coverImage: url,
      }));
      // Yükleme bitince preview'u gerçek URL ile güncelle (opsiyonel, ama iyi olur)
      setCoverImagePreview(url);
    } catch (error: any) {
      console.error("Firebase yükleme hatası:", error);
      alert(`Kapak fotoğrafı yüklenemedi: ${error.message}`);
      // Hata olursa preview'u sil
      setCoverImagePreview(null);
    }
  };

  const handleCoverImageUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;

    setFormData(prev => ({
      ...prev,
      coverImage: url,
    }));

    setCoverImagePreview(url || null);
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Dosya boyutu kontrolü (Client-side fail fast)
    for (let i = 0; i < files.length; i++) {
      if (files[i].size > MAX_FILE_SIZE) {
        alert(`Yükleme hatası: "${files[i].name}" dosyası 15MB'den büyük.`);
        return;
      }
    }

    console.log(`Galeri yüklemesi başlatılıyor... (${files.length} dosya)`);
    setGalleryUploading(true);
    try {
      const urls = await uploadMultipleImages(files, 'blog-galleries');
      console.log("Galeri yüklemesi BAŞARILI. URLler:", urls);

      setFormData(prev => ({
        ...prev,
        galleryImages: [...(prev.galleryImages || []), ...urls]
      }));
      alert(`${urls.length} galeri görseli başarıyla yüklendi!`);
    } catch (error: any) {
      console.error("Galeri yükleme hatası:", error);
      alert(`Yükleme hatası: ${error.message}`);
    } finally {
      setGalleryUploading(false);
    }
  };

  const handleRemoveGalleryImage = (indexToRemove: number) => {
    setFormData(prev => ({
      ...prev,
      galleryImages: prev.galleryImages?.filter((_, index) => index !== indexToRemove) || []
    }));
  };

  const handleGalleryImagesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const urls = e.target.value.split('\n').filter(url => url.trim() !== '');
    setFormData(prev => ({ ...prev, galleryImages: urls }));
  };

  const handleAddAffiliateLink = () => {
    const currentLinks = formData.affiliateLinks || [];
    if (currentLinks.length >= 3) {
      alert("En fazla 3 adet affiliate link ekleyebilirsiniz.");
      return;
    }
    setFormData(prev => ({
      ...prev,
      affiliateLinks: [...currentLinks, { provider: 'other', label: '', url: '' }]
    }));
  };

  const handleRemoveAffiliateLink = (index: number) => {
    setFormData(prev => ({
      ...prev,
      affiliateLinks: prev.affiliateLinks?.filter((_, i) => i !== index) || []
    }));
  };

  const handleAffiliateLinkChange = (index: number, field: keyof AffiliateLink, value: string) => {
    setFormData(prev => {
      const newLinks = [...(prev.affiliateLinks || [])];
      if (newLinks[index]) {
        newLinks[index] = { ...newLinks[index], [field]: value };
      }
      return { ...prev, affiliateLinks: newLinks };
    });
  };

  const handleCategoryChange = (cat: RecordCategory) => {
    setFormData(prev => ({ ...prev, category: cat }));
  };

  const handleStatusChange = (stat: RecordStatus) => {
    setFormData(prev => ({ ...prev, status: stat }));
  };

  const handleSave = (publish: boolean) => {
    if (!formData.title || !formData.cityName || !formData.summary) {
      alert("Lütfen Başlık, Şehir ve Özet alanlarını doldurun.");
      return;
    }

    const dataToSave = { ...formData };
    if (isTimeless) {
      dataToSave.startDate = undefined;
      dataToSave.endDate = undefined;
    }

    // DEBUG: Kaydedilecek veriyi konsola yazdır
    console.log("handleSave içinde, servise gönderilecek veri:", dataToSave);

    if (id) {
      updateRecord(dataToSave);
      alert('İçerik güncellendi!');
    } else {
      addRecord(dataToSave);
      alert('Yeni içerik eklendi!');
    }

    navigate(`${adminPath}/all-content`);
  };

  const formatDateForInput = (date?: Date): string => {
    if (!date || !isValid(date)) return '';
    return format(date, "yyyy-MM-dd'T'HH:mm");
  };

  return (
    <div className="text-white">
      <div className="flex justify-between items-center mb-8 sticky top-0 bg-blue-900/90 z-10 py-4 -mx-8 px-8 -mt-8 rounded-b-lg">
        <h1 className="text-3xl font-bold">{id ? 'İçeriği Düzenle' : 'Yeni İçerik Oluştur'}</h1>
        <div className="space-x-4">
          <button onClick={() => handleSave(false)} className="px-6 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white font-semibold transition-colors">
            Taslak Kaydet
          </button>
          <button onClick={() => handleSave(true)} className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-semibold transition-colors">
            Yayınla
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-blue-800 p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">1. Kimlik ve Konum</h2>
            <div>
              <label htmlFor="title" className="block text-blue-200 text-sm font-medium mb-2">Başlık</label>
              <input type="text" id="title" name="title" value={formData.title} onChange={handleChange} className="w-full p-3 rounded-lg bg-blue-900 border border-blue-700 focus:ring-blue-500 focus:border-blue-500 text-white text-lg font-bold" placeholder="İçerik Başlığı" />
              <p className="mt-2 text-sm text-blue-300">URL: <span className="font-mono bg-blue-900/50 px-2 py-1 rounded">{slug || 'otomatik-olusacak'}</span></p>
            </div>
            <div className="mt-4">
              <label htmlFor="cityName" className="block text-blue-200 text-sm font-medium mb-2">Şehir Seçimi</label>
              <select id="cityName" name="cityName" value={formData.cityName} onChange={handleChange} className="w-full p-3 rounded-lg bg-blue-900 border border-blue-700 focus:ring-blue-500 focus:border-blue-500 text-white">
                <option value="">Şehir Seçin</option>
                {provinces.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="mt-4">
              <label htmlFor="districtName" className="block text-blue-200 text-sm font-medium mb-2">İlçe/Mekan (Opsiyonel)</label>
              <input type="text" id="districtName" name="districtName" value={formData.districtName || ''} onChange={handleChange} className="w-full p-3 rounded-lg bg-blue-900 border border-blue-700 focus:ring-blue-500 focus:border-blue-500 text-white" placeholder="Örn: Çankaya, Kordon" />
            </div>
          </div>

          <div className="bg-blue-800 p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">3. İçerik Detayları</h2>
            <div>
              <label htmlFor="summary" className="block text-blue-200 text-sm font-medium mb-2">Özet (Kartlarda Görünecek)</label>
              <textarea id="summary" name="summary" value={formData.summary} onChange={handleChange} maxLength={160} rows={3} className="w-full p-3 rounded-lg bg-blue-900 border border-blue-700 focus:ring-blue-500 focus:border-blue-500 text-white resize-none" placeholder="Kısa özet metni (max 160 karakter)"></textarea>
              <p className="text-right text-sm text-blue-300">{summaryCharCount}/160</p>
            </div>
            <div className="mt-4">
              <label className="block text-blue-200 text-sm font-medium mb-2">Zamanlayıcı</label>
              <div className="flex items-center space-x-2 mb-2">
                <input type="checkbox" id="isTimeless" checked={isTimeless} onChange={(e) => setIsTimeless(e.target.checked)} className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out bg-blue-900 border-blue-700" />
                <label htmlFor="isTimeless" className="text-blue-200">Süresiz / Kalıcı Mekan</label>
              </div>
              {!isTimeless && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="startDate" className="block text-blue-300 text-xs font-medium mb-1">Başlangıç Tarihi/Saati</label>
                    <input type="datetime-local" id="startDate" name="startDate" value={formatDateForInput(formData.startDate)} onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value ? new Date(e.target.value) : undefined }))} className="w-full p-3 rounded-lg bg-blue-900 border border-blue-700" />
                  </div>
                  <div>
                    <label htmlFor="endDate" className="block text-blue-300 text-xs font-medium mb-1">Bitiş Tarihi/Saati</label>
                    <input type="datetime-local" id="endDate" name="endDate" value={formatDateForInput(formData.endDate)} onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value ? new Date(e.target.value) : undefined }))} className="w-full p-3 rounded-lg bg-blue-900 border border-blue-700" />
                  </div>
                </div>
              )}
            </div>
            <div className="mt-4">
              <label htmlFor="content" className="block text-blue-200 text-sm font-medium mb-2">İçerik Alanı (Rich Text Editor)</label>
              <textarea
                id="content"
                name="content"
                value={formData.content || ''}
                onChange={handleChange}
                rows={12}
                className="w-full p-3 rounded-lg bg-blue-900 border border-blue-700 focus:ring-blue-500 focus:border-blue-500 text-white resize-y min-h-[200px]"
                placeholder="İçerik metnini buraya yazınız..."
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-1 space-y-6">
          <div className="bg-blue-800 p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">2. Sınıflandırma</h2>
            <div>
              <label className="block text-blue-200 text-sm font-medium mb-2">Kategori (Tekli Seçim)</label>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button key={cat} onClick={() => handleCategoryChange(cat)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${formData.category === cat ? 'bg-blue-600 text-white shadow-md' : 'bg-blue-900 text-blue-200 hover:bg-blue-700'}`}>
                    {cat}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-blue-200 text-sm font-medium mb-2">Durum (Status)</label>
              <div className="flex flex-wrap gap-2">
                {statuses.map((stat) => (
                  <button key={stat} onClick={() => handleStatusChange(stat)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${formData.status === stat ? 'bg-blue-600 text-white shadow-md' : 'bg-blue-900 text-blue-200 hover:bg-blue-700'}`}>
                    {stat}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-blue-200 text-sm font-medium mb-2">Fiyat Tipi</label>
              <select id="priceType" name="priceType" value={formData.priceType} onChange={handleChange} className="w-full p-3 rounded-lg bg-blue-900 border border-blue-700">
                {priceTypes.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            <div className="mt-4">
              <label className="block text-blue-200 text-sm font-medium mb-2">Kaynak Tipi</label>
              <select id="sourceType" name="sourceType" value={formData.sourceType} onChange={handleChange} className="w-full p-3 rounded-lg bg-blue-900 border border-blue-700">
                {sourceTypes.map(type => <option key={type} value={type}>{type}</option>)}
              </select>
            </div>
            <div className="mt-4 flex items-center">
              <input type="checkbox" id="isFeatured" checked={formData.isFeatured} onChange={(e) => setFormData(prev => ({ ...prev, isFeatured: e.target.checked }))} className="form-checkbox h-4 w-4 text-blue-600" />
              <label htmlFor="isFeatured" className="ml-2 text-blue-200">Öne Çıkan İçerik</label>
            </div>
          </div>

          <div className="bg-blue-800 p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">5. Affiliate Linkler</h2>
            <div className="space-y-4">
              {formData.affiliateLinks?.map((link, index) => (
                <div key={index} className="bg-blue-900/50 p-4 rounded-lg border border-blue-700 relative">
                  <button
                    onClick={() => handleRemoveAffiliateLink(index)}
                    className="absolute top-2 right-2 text-red-400 hover:text-red-300"
                  >
                    Sil
                  </button>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-blue-300 text-xs mb-1">Sağlayıcı</label>
                      <select
                        value={link.provider}
                        onChange={(e) => handleAffiliateLinkChange(index, 'provider', e.target.value)}
                        className="w-full p-2 rounded bg-blue-900 border border-blue-700 text-white text-sm"
                      >
                        <option value="obilet">Obilet</option>
                        <option value="booking">Booking.com</option>
                        <option value="other">Diğer</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-blue-300 text-xs mb-1">Etiket (Buton Metni)</label>
                      <input
                        type="text"
                        value={link.label}
                        onChange={(e) => handleAffiliateLinkChange(index, 'label', e.target.value)}
                        placeholder="Örn: Otobüs Bileti Al"
                        className="w-full p-2 rounded bg-blue-900 border border-blue-700 text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-blue-300 text-xs mb-1">URL</label>
                      <input
                        type="text"
                        value={link.url}
                        onChange={(e) => handleAffiliateLinkChange(index, 'url', e.target.value)}
                        placeholder="https://..."
                        className="w-full p-2 rounded bg-blue-900 border border-blue-700 text-white text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}

              {(!formData.affiliateLinks || formData.affiliateLinks.length < 3) && (
                <button
                  onClick={handleAddAffiliateLink}
                  className="w-full py-2 border-2 border-dashed border-blue-600 text-blue-300 rounded-lg hover:bg-blue-900/50 transition-colors"
                >
                  + Yeni Link Ekle
                </button>
              )}
            </div>
          </div>

          <div className="bg-blue-800 p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">4. Görsel Yönetimi</h2>
            <div className="space-y-6">
              {/* Cover Image Upload */}
              <div>
                <label htmlFor="coverImageFile" className="block text-blue-200 text-sm font-medium mb-2">
                  Kapak Fotoğrafı
                </label>
                <input
                  type="file"
                  id="coverImageFile"
                  accept="image/*"
                  onChange={handleCoverImageFileChange}
                  className="w-full p-3 rounded-lg bg-blue-900 border border-blue-700 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-500 file:cursor-pointer"
                />

                {coverImagePreview && (
                  <div className="mt-4 rounded-xl overflow-hidden bg-blue-900/40 border border-blue-700">
                    <img
                      src={coverImagePreview}
                      alt={formData.title || 'Kapak görseli'}
                      className="w-full h-48 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, coverImage: '' }));
                        setCoverImagePreview(null);
                      }}
                      className="mt-2 w-full bg-red-600 text-white px-3 py-2 text-sm font-semibold hover:bg-red-700"
                    >
                      Kaldır
                    </button>
                  </div>
                )}

                <p className="mt-2 text-sm text-blue-300">veya URL</p>
                <input
                  type="text"
                  name="coverImageUrl"
                  value={formData.coverImage || ''}
                  onChange={handleCoverImageUrlChange}
                  className="mt-2 w-full p-3 rounded-lg bg-blue-900 border border-blue-700 text-white"
                  placeholder="https://example.com/image.jpg"
                />
              </div>

              {/* Gallery Images Upload */}
              <div>
                <label htmlFor="galleryImagesFile" className="block text-blue-200 text-sm font-medium mb-2">
                  Galeri Resimleri
                </label>
                <input
                  type="file"
                  id="galleryImagesFile"
                  accept="image/*"
                  multiple
                  onChange={handleGalleryUpload}
                  disabled={galleryUploading}
                  className="w-full p-3 rounded-lg bg-blue-900 border border-blue-700 text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-500 file:cursor-pointer"
                />
                {galleryUploading && (
                  <p className="mt-2 text-sm text-blue-300">📤 Yükleniyor...</p>
                )}
                {formData.galleryImages && formData.galleryImages.length > 0 && (
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {formData.galleryImages.map((img, index) => (
                      <div key={index} className="relative">
                        <img
                          src={img}
                          alt={`Galeri ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveGalleryImage(index)}
                          className="absolute top-1 right-1 bg-red-600 text-white w-6 h-6 rounded-full text-xs font-bold hover:bg-red-700"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="mt-2 text-sm text-blue-300">veya URL ile (her satıra bir URL)</p>
                <textarea
                  id="galleryImages"
                  name="galleryImages"
                  value={(formData.galleryImages || []).join('\n')}
                  onChange={handleGalleryImagesChange}
                  rows={4}
                  className="mt-2 w-full p-3 rounded-lg bg-blue-900 border border-blue-700 resize-y text-white"
                  placeholder="https://example.com/gallery1.jpg&#x0a;https://example.com/gallery2.jpg"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddEditContentPage;